import { useEffect, Component, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import LoginPage from "@/pages/LoginPage";
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const CustomersPage = lazy(() => import("@/pages/CustomersPage"));
const ProductsPage = lazy(() => import("@/pages/ProductsPage"));
const SalesPage = lazy(() => import("@/pages/SalesPage"));
const SuppliersPage = lazy(() => import("@/pages/SuppliersPage"));
const ExpensesPage = lazy(() => import("@/pages/ExpensesPage"));
const PurchasesPage = lazy(() => import("@/pages/PurchasesPage"));
const ScrapPage = lazy(() => import("@/pages/ScrapPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const LogsPage = lazy(() => import("@/pages/LogsPage"));
const VisitorsPage = lazy(() => import("@/pages/VisitorsPage"));
const NotesPage = lazy(() => import("@/pages/NotesPage"));
const MarketingPage = lazy(() => import("@/pages/MarketingPage"));
const VasooliPage = lazy(() => import("@/pages/VasooliPage"));
const PaymentsPage = lazy(() => import("@/pages/PaymentsPage"));

/* /gst -> GST wala bahi-khaata chalu karke login par bhejo.
   Bina GST wale khaate se bilkul alag database. */
function GstEntry() {
  useEffect(() => {
    try {
      localStorage.setItem("it_book", "gst");
      localStorage.removeItem("it_token");        // dono khaate ka login alag
      localStorage.removeItem("it_admin_session");
    } catch { /* ignore */ }
    window.location.replace("/login");
  }, []);
  return null;
}


/* Kabhi koi gadbad ho to safed screen ki jagah saaf message + Reload */
class ErrorBoundary extends Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    try {
      // eslint-disable-next-line no-console
      console.error("[IT-ERP crash]", err, info);
      const log = { at: new Date().toISOString(), page: window.location.pathname,
                    msg: String(err && err.message || err), stack: String(err && err.stack || "").slice(0, 1200) };
      localStorage.setItem("it_last_crash", JSON.stringify(log));
    } catch { /* ignore */ }
  }
  hardReload = async () => {
    try {
      if ("caches" in window) { const k = await caches.keys(); await Promise.all(k.map((x) => caches.delete(x))); }
      if (navigator.serviceWorker) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch { /* ignore */ }
    window.location.reload(true);
  };
  render() {
    if (!this.state.err) return this.props.children;
    const m = String(this.state.err && this.state.err.message || this.state.err);
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="glass-strong rounded-3xl p-8 max-w-lg text-center">
          <div className="text-4xl mb-3">😕</div>
          <h2 className="display text-xl">This page couldn't open</h2>
          <p className="mono text-[12.5px] text-white/60 mt-2 leading-relaxed">
            This usually happens when an old copy gets stuck in the browser. Press the button below to clear everything and load the new app.
          </p>
          <div className="flex flex-wrap justify-center gap-2 mt-5">
            <button onClick={this.hardReload}
              className="rounded-full px-5 py-2.5 text-[13px] font-semibold text-black bg-gradient-to-r from-cyan-400 to-purple-500">
              🔄Clear and reopen
            </button>
            <a href="/" className="glass rounded-full px-5 py-2.5 text-[13px]">Dashboard</a>
          </div>
          <details className="mt-5 text-left">
            <summary className="mono text-[10.5px] text-white/35 cursor-pointer">technical detail</summary>
            <pre className="mono text-[10px] text-white/45 mt-2 whitespace-pre-wrap break-words">{m}</pre>
          </details>
        </div>
      </div>
    );
  }
}

function PermGate({ perm, children }) {
  const { user, can } = useAuth();
  if (!user) return children;
  if (!perm || can(perm)) return <ErrorBoundary>{children}</ErrorBoundary>;
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#030305] px-6">
      <div className="glass-strong rounded-3xl p-8 max-w-md text-center">
        <div className="text-4xl mb-3">🔒</div>
        <h2 className="display text-xl">You don't have permission for this page</h2>
        <p className="mono text-[12px] text-white/55 mt-2 leading-relaxed">
          This section isn't enabled for your role. If you need it, ask the owner — they can grant it from Admin → Roles & Permissions.
        </p>
        <a href="/" className="inline-block mt-5 pill-neon rounded-full px-5 py-2 text-[13px]">Dashboard par jao</a>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030305]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-2 border-white/10 border-t-cyan-400 animate-spin" />
          <p className="mono text-[10.5px] uppercase tracking-[0.3em] text-white/45">Authenticating</p>
        </div>
      </div>
    );
  }
  // Full ERP requires admin session (came in via Admin login, not quick-PIN)
  const isAdminSession = localStorage.getItem("it_admin_session") === "1";
  return user && isAdminSession ? children : <Navigate to="/login" replace />;
}

export default function App() {
  // Number inputs: focus par 0 select ho jata hai — type karte hi replace (keyboard-first)
  useEffect(() => {
    const onFocus = (e) => {
      const t = e.target;
      if (t && t.tagName === "INPUT" && t.type === "number") {
        requestAnimationFrame(() => { try { t.select(); } catch {} });
      }
    };
    const onClick = (e) => {
      const t = e.target && e.target.closest ? e.target.closest('input[type="date"]') : null;
      if (t && typeof t.showPicker === "function") { try { t.showPicker(); } catch {} }
    };
    document.addEventListener("focusin", onFocus);
    document.addEventListener("click", onClick);
    return () => { document.removeEventListener("focusin", onFocus); document.removeEventListener("click", onClick); };
  }, []);

  // idle auto-logout: 30 min no activity -> sign out
  useEffect(() => {
    let _it;
    var IDLE_MS = 30 * 60 * 1000;
    const _reset = () => {
      clearTimeout(_it);
      _it = setTimeout(() => {
        try {
          if (localStorage.getItem("it_token")) {
            localStorage.removeItem("it_token");
            localStorage.removeItem("it_admin_session");
            window.location.href = "/login";
          }
        } catch (e) {}
      }, IDLE_MS);
    };
    const _evs = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    _evs.forEach((ev) => window.addEventListener(ev, _reset, { passive: true }));
    _reset();
    return () => { clearTimeout(_it); _evs.forEach((ev) => window.removeEventListener(ev, _reset)); };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<div className="min-h-screen bg-[#030305]" />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
        <Route path="/gst" element={<GstEntry />} />
            <Route path="/" element={<Protected><DashboardPage /></Protected>} />
            <Route path="/customers" element={<Protected><PermGate perm="customers.view"><CustomersPage /></PermGate></Protected>} />
<Route path="/visitors" element={<Protected><PermGate perm="customers.view"><VisitorsPage /></PermGate></Protected>} />
<Route path="/notes" element={<Protected><PermGate perm="customers.view"><NotesPage /></PermGate></Protected>} />
<Route path="/marketing" element={<Protected><PermGate perm="customers.view"><MarketingPage /></PermGate></Protected>} />
            <Route path="/products" element={<Protected><PermGate perm="products.view"><ProductsPage /></PermGate></Protected>} />
            <Route path="/scrap" element={<Protected><PermGate perm="scrap.view"><ScrapPage /></PermGate></Protected>} />
            <Route path="/sales" element={<Protected><PermGate perm="sales.view"><SalesPage /></PermGate></Protected>} />
            <Route path="/suppliers" element={<Protected><PermGate perm="purchases.view"><SuppliersPage /></PermGate></Protected>} />
            <Route path="/expenses" element={<Protected><PermGate perm="expenses.view"><ExpensesPage /></PermGate></Protected>} />
            <Route path="/purchases" element={<Protected><PermGate perm="purchases.view"><PurchasesPage /></PermGate></Protected>} />
            <Route path="/reports" element={<Protected><PermGate perm="reports.daily"><ReportsPage /></PermGate></Protected>} />
            <Route path="/logs" element={<Protected><PermGate perm="logs.view"><LogsPage /></PermGate></Protected>} />
            <Route path="/admin" element={<Protected><PermGate perm="admin.company"><AdminPage /></PermGate></Protected>} />
            <Route path="/collections" element={<Protected><PermGate perm="collections.view"><VasooliPage /></PermGate></Protected>} />
            <Route path="/payments" element={<Protected><PermGate perm="sales.collect"><PaymentsPage /></PermGate></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
